import React, { useCallback, useEffect, useState } from 'react';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Copy, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export const ShareLinkCard = ({ orderId, refreshKey }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const res = await authFetch(`${API_BASE}/api/share/admin/orders/${orderId}`);
    if (res.ok) setInfo(await res.json());
  }, [orderId]);

  useEffect(() => {
    load();
    const t = setTimeout(load, 6000);
    return () => clearTimeout(t);
  }, [load, refreshKey]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.share_url);
      setCopied(true);
      toast.success('Share link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy automatically. Select the link and copy it.");
    }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/api/share/admin/orders/${orderId}/regenerate`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to regenerate');
      setInfo(data);
      toast.success(`Share image ready (${(data.share_image.bytes / 1024).toFixed(0)} KB)`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!info?.share_url) return null;
  const img = info.share_image;
  return (
    <div className="space-y-3 pt-4 border-t border-white/10" data-testid="share-link-card">
      <Label className="text-[#E8E4DE]/80 text-sm font-medium">Social Share Link</Label>
      <p className="text-[#E8E4DE]/50 text-xs" data-testid="share-image-status">
        {img ? `Preview image 1200×630 · ${(img.bytes / 1024).toFixed(0)} KB` : 'No preview image yet'}
        {!info.is_public && ' · Link works once the property is paid and in the portfolio'}
      </p>
      <div className="flex gap-2">
        <Input value={info.share_url} readOnly className="bg-[#050816]/50 border-white/10 text-white font-mono text-xs" data-testid="share-link-input" />
        <Button onClick={copy} variant="outline" className="border-[#C6A14A]/40 text-[#C6A14A] hover:bg-[#C6A14A]/10 shrink-0" data-testid="copy-share-link-btn">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          <span className="ml-2 hidden sm:inline">Copy Share Link</span>
        </Button>
      </div>
      <Button onClick={regenerate} disabled={busy} variant="ghost" size="sm" className="text-[#E8E4DE]/70 hover:bg-white/5" data-testid="regenerate-share-image-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
        Regenerate share image
      </Button>
    </div>
  );
};
